import { useState } from "react";
import { z } from "zod";
import {
  MiDetailFields,
  MiModal,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  INPUT_TYPES,
  TIMEZONE_OPTIONS,
  regionCodesUS,
} from "@miplatform/constants";
import { useEdit, useMapForm } from "@miplatform/hooks";
import { useCityViewModel } from "@miplatform/view-models";
import { mdiCityVariant, mdiClose } from "@mdi/js";
import { useAddCityViewModel } from "./useAddCityViewModel";

const validationRules = [
  {
    name: "regionCode",
    validationRule: z.string().min(1, "State is required"),
  },
  {
    name: "county",
    validationRule: z.string().min(1, "County is required"),
  },
  {
    name: "city",
    validationRule: z.string().min(1, "City is required"),
  },
  {
    name: "timeZone",
    validationRule: z.string().min(1, "Timezone is required"),
  },
];

export const AddCityModal = ({ open, onClose, onCreate }) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const edit = useEdit(
    {
      regionCode: "",
      city: "",
      latitude: 39.8283,
      longitude: -98.5795,
      timeZone: "",
    },
    validationRules
  );

  const { cities, isLoading, getCounty, addCityToState, getCities } =
    useCityViewModel();
  const { addCity } = useAddCityViewModel({ edit, onCreate });

  const city = edit.getValue("city");
  const county = edit.getValue("county");
  const regionCode = edit.getValue("regionCode");
  const timeZone = edit.getValue("timeZone");

  const { citiesOptions, isCityValid, handleCityTextChange } = useMapForm({
    values: edit.getValue(),
    update: edit.update,
    cities,
    addCity: addCityToState,
    getCities,
    isEditing: true,
  });

  const handleCityChange = async (value) => {
    let countyName = null;

    try {
      countyName = await getCounty(value, regionCode);
    } catch {
      /* empty */
    }

    if (countyName) {
      edit.update({ city: value, county: countyName });
    }
  };

  const cityFields = [
    {
      label: "State",
      name: "regionCode",
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: regionCodesUS.map((code) => ({ label: code, value: code })),
      width: "50%",
    },
    {
      label: "County",
      name: "county",
      required: true,
      width: "50%",
    },
    {
      label: "City",
      name: "city",
      required: true,
      fieldType: INPUT_TYPES.AUTOCOMPLETE,
      disableClearable: true,
      value: city,
      disabled: !regionCode,
      onInputChange: (_, value) => {
        handleCityTextChange(value);
        edit.update({ city: value });
      },
      handleChange: (_, option) => {
        if (option) {
          edit.update({ city: option.value });
          handleCityChange(option.value);
        }
      },
      options: citiesOptions,
      width: "50%",
    },
    {
      label: "Timezone",
      name: "timeZone",
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: TIMEZONE_OPTIONS,
      width: "100%",
    },
  ];

  const handleModalOpen = () => setIsConfirmationModalOpen(true);

  const handleModalClose = () => setIsConfirmationModalOpen(false);

  const handleCancel = () => {
    edit.reset();
    onClose();
  };

  const handleAddCity = async () => {
    const res = await addCity({
      city,
      county,
      regionCode,
      isValid: false,
      timeZone,
    });

    if (res) {
      handleCancel();
    }
  };

  const handleModalPositive = async () => {
    handleModalClose();
    const res = await addCity({
      city,
      county,
      regionCode,
      isValid: false,
      timeZone,
    });

    if (res) {
      handleCancel();
    }
  };

  const handleSubmit = async () => {
    if (!isCityValid) {
      handleModalOpen();
      return;
    }

    handleAddCity();
  };

  return (
    <MiModal
      title="Add City"
      variant="temporary"
      open={open}
      closeModal={handleCancel}
      leftIcon={mdiCityVariant}
      rightIcon={mdiClose}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleCancel,
        },
        {
          name: "Submit",
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          disabled: !edit.isValid() || isLoading,
          onClick: handleSubmit,
        },
      ]}
    >
      <MiDetailFields edit={edit} detailFields={cityFields} />
      <MiModalConfirm
        open={isConfirmationModalOpen}
        textPositive="Yes"
        handleNegative={handleModalClose}
        handlePositive={handleModalPositive}
        description={`${city} is not found within our records, are you sure you want to proceed?`}
      />
    </MiModal>
  );
};
